import {Button, Card, CardContent, CardHeader, Grid, Typography} from "@mui/material";

import {
    Addresses,
    CompanyActivity,
    InfoExtra,
    Personal,
    Phones,
    SuretiesShareholdingStructure,
} from "./components/components-company-data";

import React, {Fragment, useContext, useEffect, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {CompanyContext, CompanyContextValue} from "../context/companyContext";
import KeyboardBackspaceOutlinedIcon from "@mui/icons-material/KeyboardBackspaceOutlined";
import {useNavigate} from "react-router-dom";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import toast from "react-hot-toast";
import {
    Client, ClientAfipActivity,
    ClientFields,
    ClientForm,
    ClientRelationshipInsert,
    ClientRelationshipInsertFields,
    ClientType,
    CoOwnerRelationshipWithoutSolicitation,
    CoOwnerRelationshipWithoutSolicitationFields,
    MailUpdateDTO,
    MailUpdateDTOFields
} from "../../../types/client/clientData";
import {
    Solicitation,
    SolicitationClientInsertDTO,
    SolicitationFields
} from "../../../types/solicitation/solicitationData";
import {HttpOnboarding} from "../../../http/onboarding/httpOnboarding";
import {Channels, MaritalStates, Modules} from "../../../types/generalData";
import {HttpClientAfipActivity} from "../../../http/cache/client/httpClientAfipActivity";
import {
    MailRequiredSchema,
    RequiredPositiveNumberSchema,
    WebsiteSchema
} from "../../../utils/validations";
import {LoaderBlockUI} from "../../../components/loader";
import {EntityWithIdFields} from "../../../types/baseEntities";
import {HttpSolicitation, HttpSolicitationClient} from "../../../http/solicitation/httpSolicitation";
import {HttpClient, HttpClientRelationships} from "../../../http/client/httpClient";
import {Icon} from "@iconify/react";
import {numberFormatter} from "../../../utils/formatters/numberFormatter";
import {
    SolicitationGuaranteeCodes,
    SolicitationStateCodes,
    SolicitationTypeCodes
} from "../../../types/solicitation/solicitationEnums";
import PersonalMarital from "./components/components-company-data/PersonalMarital";
import {ClientRelationshipClassificationCodes, ClientRelationshipCodes} from "../../../types/client/clientEnums";
import CompanySolicitationActivity from "./components/components-company-data/CompanySolicitationActivity";
import {HttpSolicitationOnboarding} from "../../../http/solicitation/httpSolicitationOnboarding";

interface StepCompanyDataProps {
    client?: Client,
    handleNext: () => void,
    solicitation?: Solicitation
}

const StepCompanyData = ({ client, handleNext, solicitation }: StepCompanyDataProps) => {
    const { clientSolicitationId, setClientSolicitationId } = useContext<CompanyContextValue>(CompanyContext);
    const navigate = useNavigate()
    const [coOwners, setCoOwners] = useState<CoOwnerRelationshipWithoutSolicitation[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [fiance, setFiance] = useState<Client>()
    const [coOwnersCheck, setCoOwnersCheck] = useState<boolean>(true)
    const [activities, setActivities] = useState<ClientAfipActivity[]>()
    const [phonesCompleted, setPhonesCompleted] = useState<boolean>(false)

    const CompanyDataSchema = yup.object().shape({
        [SolicitationFields.EmployeeQuantity]: RequiredPositiveNumberSchema,
        [ClientFields.Website]: WebsiteSchema,
        [SolicitationFields.Mail]: MailRequiredSchema,
    })
        .test(ClientFields.ClientClassificationCode, 'Campo Obligatorio', (obj) => {
            if (client && client[ClientFields.ClientTypeCode] === ClientType.Fisica) return true;
            
            // @ts-ignore
            if (obj && obj[ClientFields.ClientClassificationCode]) return true;

            return new yup.ValidationError(
                'Campo Obligatorio',
                null,
                ClientFields.ClientClassificationCode,
            );
        })
    
    useEffect(() => {
        if(client){
            HttpClientAfipActivity.getByIdClient(client[EntityWithIdFields.Id]).then((r) => {
                setActivities(r)
            })
            methods.setValue(SolicitationFields.Mail, client[ClientFields.LastMail])
        }
    }, [client])
    
    useEffect(() => {
        if(solicitation || clientSolicitationId !== 0) {
            solicitation && methods.setValue(SolicitationFields.AfipActivityCode, solicitation[SolicitationFields.AfipActivityCode])
            solicitation && methods.setValue(SolicitationFields.MonotaxCategory, solicitation[SolicitationFields.MonotaxCategory])
            solicitation && methods.setValue(SolicitationFields.IncomeCertifications, solicitation[SolicitationFields.IncomeCertifications])
        }
    }, [solicitation])
    
    const methods = useForm<SolicitationClientInsertDTO>({
        resolver: yupResolver(CompanyDataSchema),
        defaultValues: {
            [ClientFields.Website]: client?.[ClientFields.Website],
            [ClientFields.CommercialName]: client?.[ClientFields.CommercialName],
            [ClientFields.MaritalStatusCode]: client?.[ClientFields.MaritalStatusCode],
            [SolicitationFields.AfipActivityCode]: solicitation?.[SolicitationFields.AfipActivityCode]
        }
    });
    
    useEffect(() => {
        if (methods.formState.errors[SolicitationFields.EmployeeQuantity]) {
            toast.error('Por favor completa los campos');
        }
        
        if (methods.formState.errors[SolicitationFields.Mail]) {
            toast.error('Debe completar el mail de contacto para continuar')
        }
        
        if (methods.formState.errors[ClientFields.ClientClassificationCode]) {
            toast.error('Debe completar el campo de clasificación para continuar')
        }
    }, [methods.formState.errors])
    
    const onSaveFiance = (c: Client) => setFiance(c)

    const onSubmit = async (data: SolicitationClientInsertDTO) => {
        if (!coOwnersCheck) {
            toast.error("Para continuar debe insertar cotitulares y/o marcar que no posee");
            
            return;
        }
        if (!phonesCompleted) {
            toast.error('Verifique que haya cargado celular y/o teléfono particular');
            
            return;
        }
        
        if (client?.[ClientFields.ClientTypeCode] === ClientType.Fisica && !data[ClientFields.BirthDate]) {
            toast.error("Para continuar debe completar la fecha de nacimiento")
            
            return;
        }
        
        const clientData: ClientForm = {
            ...client as ClientForm,
            [ClientFields.AfipResponsibilityTypeCode]: data[ClientFields.AfipResponsibilityTypeCode] || null,
            [ClientFields.MaritalStatusCode]: data[ClientFields.MaritalStatusCode] || client?.[ClientFields.MaritalStatusCode] || 1,
            [ClientFields.Website]: data[ClientFields.Website],
            [ClientFields.CommercialName]: data[ClientFields.CommercialName],
            [ClientFields.ClientClassificationCode]: data[ClientFields.ClientClassificationCode] ?? 0
        };

        const submitData: SolicitationClientInsertDTO = {
            ...data,
            [SolicitationFields.TitularBusinessName]: client?.[ClientFields.BusinessName],
            [SolicitationFields.TitularCUIT]: client?.[ClientFields.CUIT],
            [SolicitationFields.TitularId]: client?.[EntityWithIdFields.Id],
            [SolicitationFields.SolicitationChannelCode]: Channels.Onboarding,
            [SolicitationFields.BankCode]: null,
            [SolicitationFields.BranchCode]: null,
            [SolicitationFields.SolicitationStateCode]: SolicitationStateCodes.Pendant,
            [SolicitationFields.SolicitationTypeCode]: SolicitationTypeCodes.TraditionalExpress,
            [SolicitationFields.AnualIncome]: 0,
            [SolicitationFields.OriginId]: '',
            [SolicitationFields.SolicitationGuaranteeTypeCode]: SolicitationGuaranteeCodes.MoreSureties,
            [SolicitationFields.CommunicationChannelCode]: null,
            [SolicitationFields.InstallmentCTAmount]: null,
            [SolicitationFields.InstallmentInvAmount]: null,
            [SolicitationFields.InstallmentAgroAmount]: null,
            [SolicitationFields.CommercialId]: null,
            [SolicitationFields.ModuleCode]: Modules.SolicitationOnboarding
        };

        const mailDataPost: MailUpdateDTO = {
            [MailUpdateDTOFields.ClientId]: client?.[EntityWithIdFields.Id] || 0,
            [MailUpdateDTOFields.SolicitationId]: clientSolicitationId || 0,
            [MailUpdateDTOFields.Mail]: data[SolicitationFields.Mail]
        };

        const promises: Promise<any>[] = [];

        if (fiance && client) {
            const relationshipType = data[ClientFields.MaritalStatusCode]
                ? data[ClientFields.MaritalStatusCode] === MaritalStates.Convivientes
                    ? ClientRelationshipCodes.Cohabitant
                    : ClientRelationshipCodes.Fiance
                : client?.[ClientFields.MaritalStatusCode] === MaritalStates.Convivientes
                    ? ClientRelationshipCodes.Cohabitant
                    : ClientRelationshipCodes.Fiance;

            const relacionConyuge: ClientRelationshipInsert[] = [{
                [ClientRelationshipInsertFields.Participation]: 0,
                [ClientRelationshipInsertFields.RelationshipTypeCode]: relationshipType,
                [ClientRelationshipInsertFields.RelationshipClassificationTypeCode]: ClientRelationshipClassificationCodes.RelatedPeople,
                [ClientRelationshipInsertFields.Signer]: false
            }];

            promises.push(
                HttpClientRelationships.postRelacion(
                    client[EntityWithIdFields.Id],
                    fiance[EntityWithIdFields.Id],
                    relacionConyuge
                )
            );
        }

        try {
            setLoading(true);

            if (!clientSolicitationId || clientSolicitationId === 0 || !solicitation) {
                const solicitationId = await HttpSolicitation.insert(submitData);
                localStorage.setItem('solicitationId', `${solicitationId}`);
                mailDataPost[MailUpdateDTOFields.SolicitationId] = solicitationId;

                if (coOwners.length > 0) {
                    await processCoOwners(solicitationId, coOwners, promises, clientData, mailDataPost);
                } else {
                    promises.push(HttpClient.update(clientData));
                    promises.push(HttpOnboarding.updateClientMail(mailDataPost));
                }
            }
            else {
                const currentSolicitationData: SolicitationClientInsertDTO = {
                    ...solicitation as SolicitationClientInsertDTO,
                    [SolicitationFields.AfipActivityCode]: data[SolicitationFields.AfipActivityCode],
                    [SolicitationFields.NewEmployeeQuantity]: data[SolicitationFields.NewEmployeeQuantity],
                    [SolicitationFields.EmployeeQuantity]: data[SolicitationFields.EmployeeQuantity],
                    [SolicitationFields.GenderQuota]: data[SolicitationFields.GenderQuota],
                    [SolicitationFields.Mail]: data[SolicitationFields.Mail],
                    [SolicitationFields.IncomeCertifications]: data[SolicitationFields.IncomeCertifications],
                    [SolicitationFields.MonotaxCategory]: data[SolicitationFields.MonotaxCategory],
                    [EntityWithIdFields.Id]: clientSolicitationId
                };

                promises.push(HttpSolicitation.update(currentSolicitationData));
                promises.push(HttpClient.update(clientData));
                promises.push(HttpOnboarding.updateClientMail(mailDataPost));
            }

            await Promise.all(promises);
            const result = await HttpSolicitationOnboarding.getStepResult(
                clientSolicitationId || mailDataPost[MailUpdateDTOFields.SolicitationId],
                1
            );

            if (!result.tieneError) {
                handleNext();
            } else {
                toast.error(result.descripcionError);
            }
        } catch (error) {
            toast.error('Ocurrió un error');
        } finally {
            setLoading(false);
            if (!clientSolicitationId && mailDataPost[MailUpdateDTOFields.SolicitationId]) {
                setClientSolicitationId(mailDataPost[MailUpdateDTOFields.SolicitationId]);
            }
        }
    };
    const processCoOwners = async (
        solicitationId: number,
        coOwners: any[],
        promises: Promise<any>[],
        clientData: ClientForm,
        mailDataPost: MailUpdateDTO
    ) => {
        for (const coOwner of coOwners) {
            promises.push(
                HttpSolicitationClient.insertClientRelated(
                    solicitationId,
                    coOwner[ClientFields.Id],
                    coOwner[ClientFields.CUIT],
                    coOwner[ClientFields.BusinessName],
                    coOwner[CoOwnerRelationshipWithoutSolicitationFields.CoOwnerRelationship]
                )
            );

            if (coOwner[CoOwnerRelationshipWithoutSolicitationFields.MaritalRelationship]?.length > 0) {
                promises.push(
                    HttpClientRelationships.postRelacion(
                        coOwner[ClientFields.Id],
                        coOwner[CoOwnerRelationshipWithoutSolicitationFields.FianceId],
                        coOwner[CoOwnerRelationshipWithoutSolicitationFields.MaritalRelationship]
                    )
                );
            }

            promises.push(HttpClient.update(clientData));
            promises.push(HttpOnboarding.updateClientMail(mailDataPost));
        }
    };
    
    const watchWebsite = methods.watch(ClientFields.Website)

    useEffect(() => {
        if (watchWebsite == '') methods.setValue(ClientFields.Website, undefined)
    }, [watchWebsite]);

    return (
        <Card>
            {client && 
                <Fragment>
                    <CardHeader title={<Typography fontSize={24} fontWeight={600} alignItems='center'>{client[ClientFields.BusinessName]}</Typography>}
                                subheader={<Typography fontSize={16} color={'#7e8299'}>{numberFormatter.toCuitString(client[ClientFields.CUIT])}</Typography>}
                                action={
                                    <Button color='secondary' variant='contained' onClick={() => navigate('/solicitudes')} startIcon={<KeyboardBackspaceOutlinedIcon/>}>
                                        Inicio
                                    </Button>
                                }
                    />
                    <CardContent>
                        <FormProvider {...methods}>
                            <form onSubmit={methods.handleSubmit(onSubmit)}>
                                <Grid container rowSpacing={10} columnSpacing={20}>
                                    <Grid item xs={12}>
                                        <InfoExtra client={client} solicitationId={solicitation?.[EntityWithIdFields.Id]} />
                                    </Grid>
                                    <Grid item xs={6} >
                                        <Addresses client={client} canEdit/>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Phones clientId={client[EntityWithIdFields.Id]}
                                                canEdit
                                                isPhyisicalPerson={client[ClientFields.ClientTypeCode] === ClientType.Fisica}
                                                updatePhones={setPhonesCompleted}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <CompanyActivity client={client}/>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Personal solicitation={solicitation}/>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <CompanySolicitationActivity activities={activities} />
                                    </Grid>
                                    {client[ClientFields.ClientTypeCode] === ClientType.Fisica &&
                                        <Grid item xs={12}>
                                            <PersonalMarital client={client} onSaveFiance={onSaveFiance}/>
                                        </Grid>
                                    }
                                    <Grid item xs={12} >
                                        <SuretiesShareholdingStructure client={client}
                                                                       coOwnersFront={coOwners}
                                                                       setCoOwnersFront={setCoOwners}
                                                                       setCoOwnersCheck={setCoOwnersCheck}
                                                                       solicitationId={solicitation?.[EntityWithIdFields.Id]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} mt={2} textAlign={'end'}>
                                        <Button variant='contained' color='primary' endIcon={<Icon icon='mdi:chevron-right' fontSize={20} />} type='submit'>
                                            Siguiente
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        </FormProvider>
                    </CardContent>
                </Fragment>
            }
            {loading && <LoaderBlockUI/>}
        </Card>
    )
}


export default StepCompanyData